import React from 'react';
import { HostDirectoryService } from 'mesh-microkernel';
import { SettingsIcon, MailIcon, ShieldCheckIcon, ZapIcon } from './icons';

interface NodeMetricsFooterProps {
  nodeId: string;
  directory: HostDirectoryService;
  onOpenSettings: () => void;
  unreadNotifications: number;
  onToggleNotifications: () => void;
  isMobile?: boolean;
}

export const NodeMetricsFooter: React.FC<NodeMetricsFooterProps> = ({
  onOpenSettings,
  unreadNotifications,
  onToggleNotifications,
  isMobile = false,
}) => {
  return (
    <footer
      style={{
        height: 'var(--app-footer-height, 40px)',
        backgroundColor: 'var(--theme-bg-sidebar, #0b0f19)',
        borderTop: '1px solid var(--theme-border, #1e293b)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 8px',
        fontSize: '11px',
        color: 'var(--theme-text-secondary, #64748b)',
        flexShrink: 0,
        zIndex: 50,
        userSelect: 'none',
        gap: '8px',
      }}
    >
      {/* Bottom-Left: Settings icon + status pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        {/* Settings icon — icon only, tooltip up */}
        <button
          onClick={onOpenSettings}
          className="has-tooltip tooltip-up"
          data-tooltip="Node Dashboard & Settings"
          aria-label="Open Node Dashboard and User Settings"
          style={{
            background: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border, #1e293b)',
            borderRadius: '5px',
            padding: '4px 7px',
            color: 'var(--theme-accent, #60a5fa)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <SettingsIcon size={13} color="currentColor" />
        </button>

        {!isMobile && (
          <>
            <div style={{ width: '1px', height: '14px', backgroundColor: 'var(--theme-border, #1e293b)', flexShrink: 0 }} />

            <span
              className="has-tooltip tooltip-up"
              data-tooltip="Active context: Global Default routing"
              style={{ color: 'var(--theme-text-secondary, #94a3b8)', fontWeight: 600, letterSpacing: '0.03em', fontSize: '10px' }}
            >
              GLOBAL
            </span>

            <div style={{ width: '1px', height: '14px', backgroundColor: 'var(--theme-border, #1e293b)', flexShrink: 0 }} />

            <span
              className="has-tooltip tooltip-up"
              data-tooltip="RAG Engine: Ready for inference"
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--theme-success, #34d399)', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ color: 'var(--theme-success, #34d399)', fontWeight: 600, fontSize: '10px' }}>RAG</span>
            </span>

            <div style={{ width: '1px', height: '14px', backgroundColor: 'var(--theme-border, #1e293b)', flexShrink: 0 }} />

            <span
              className="has-tooltip tooltip-up"
              data-tooltip="Mesh network: Connected"
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--theme-accent, #60a5fa)', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ color: 'var(--theme-accent, #60a5fa)', fontWeight: 600, fontSize: '10px' }}>MESH</span>
            </span>

            <div style={{ width: '1px', height: '14px', backgroundColor: 'var(--theme-border, #1e293b)', flexShrink: 0 }} />

            <span
              className="has-tooltip tooltip-up"
              data-tooltip="Herx kernel v1.0.4 — Native Electron"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <span style={{ color: 'var(--theme-accent, #60a5fa)', fontWeight: 700, fontSize: '10px' }}>
                v1.0.4 ({import.meta.env.VITE_GIT_COMMIT})
              </span>
              <span style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: 'var(--theme-accent, #60a5fa)', padding: '0 5px', borderRadius: '3px', border: '1px solid rgba(59, 130, 246, 0.35)', fontSize: '9px', fontWeight: 700 }}>
                NATIVE
              </span>
            </span>
          </>
        )}
      </div>

      {/* Bottom-Right: Security status + Notifications icon */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {!isMobile && (
          <>
            <span
              className="has-tooltip tooltip-up"
              data-tooltip="Sovereign Mesh — P2P Native, E2E Encrypted"
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <ZapIcon size={11} color="var(--theme-warning, #f59e0b)" />
              <span style={{ color: 'var(--theme-success, #34d399)', fontSize: '10px', fontWeight: 600 }}>ACTIVE</span>
            </span>

            <div style={{ width: '1px', height: '14px', backgroundColor: 'var(--theme-border, #1e293b)' }} />

            <span
              className="has-tooltip tooltip-up"
              data-tooltip="WebCrypto AES-256-GCM — End-to-End Encrypted"
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <ShieldCheckIcon size={12} color="var(--theme-success, #34d399)" />
              <span style={{ color: 'var(--theme-success, #34d399)', fontSize: '10px', fontWeight: 600 }}>E2E</span>
            </span>

            <div style={{ width: '1px', height: '14px', backgroundColor: 'var(--theme-border, #1e293b)' }} />
          </>
        )}

        {/* Notifications icon — icon only, badge stays (data not label), tooltip up */}
        <button
          onClick={onToggleNotifications}
          className="has-tooltip tooltip-up"
          data-tooltip={unreadNotifications > 0 ? `${unreadNotifications} unread notifications` : 'Notifications inbox'}
          aria-label={unreadNotifications > 0 ? `${unreadNotifications} unread notifications` : 'Notifications inbox'}
          style={{
            position: 'relative',
            background: 'var(--theme-bg-card, #151d2a)',
            border: '1px solid var(--theme-border, #1e293b)',
            borderRadius: '5px',
            padding: '4px 7px',
            color: unreadNotifications > 0 ? 'var(--theme-accent, #60a5fa)' : 'var(--theme-text-secondary, #64748b)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <MailIcon size={13} color="currentColor" />
          {unreadNotifications > 0 && (
            <span
              style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                backgroundColor: 'var(--theme-danger, #ef4444)',
                color: 'white',
                fontSize: '9px',
                fontWeight: 800,
                minWidth: '15px',
                height: '15px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 3px',
              }}
            >
              {unreadNotifications}
            </span>
          )}
        </button>
      </div>
    </footer>
  );
};
